import {
  CreditPackage,
  CreditsService,
  StripeConnectService,
  TalentManagerEngagement,
  TalentManagerEngagementService,
  TalentManagerProfile,
  TalentManagerService,
  TalentManagerTimeService,
  TEngagementCreditBalanceResponse,
  TLogManualTimeRequest,
  TParseResumeResponse,
  TRegisterTalentManagerRequest,
} from '@talon/web/lib/api';
import { action, makeObservable, observable, runInAction } from 'mobx';

import { RootStore } from '.';
import { BaseStore } from './baseStore';

export class TalentManagerStore extends BaseStore {
  profiles: TalentManagerProfile[] = [];
  myProfile: TalentManagerProfile | null = null;
  groupEngagements: TalentManagerEngagement[] = [];
  myEngagements: TalentManagerEngagement[] = [];
  creditPackages: CreditPackage[] = [];
  isLoading = false;

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      profiles: observable,
      myProfile: observable,
      groupEngagements: observable,
      myEngagements: observable,
      creditPackages: observable,
      isLoading: observable,
      reset: action,
    });
  }

  reset = (): void => {
    this.profiles = [];
    this.myProfile = null;
    this.groupEngagements = [];
    this.myEngagements = [];
    this.creditPackages = [];
    this.isLoading = false;
  };

  fetchMarketplace = async (): Promise<void> => {
    try {
      const { talentManagerProfiles } = await this.q(TalentManagerService.listTalentManagers());
      runInAction(() => {
        this.profiles = talentManagerProfiles;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  fetchAllProfiles = async (): Promise<void> => {
    try {
      const { talentManagerProfiles } = await this.q(TalentManagerService.listAllTalentManagers());
      runInAction(() => {
        this.profiles = talentManagerProfiles;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  fetchMyProfile = async (): Promise<void> => {
    try {
      const [profileResult, engagementsResult] = await Promise.allSettled([
        this.q(TalentManagerService.getMyTalentManager()),
        this.q(TalentManagerEngagementService.listMyEngagements()),
      ]);
      runInAction(() => {
        if (profileResult.status === 'fulfilled') {
          this.myProfile = profileResult.value.talentManagerProfile;
        } else {
          this.myProfile = null;
        }
        if (engagementsResult.status === 'fulfilled') {
          this.myEngagements = engagementsResult.value.engagements;
        } else {
          this.myEngagements = [];
        }
      });
    } catch (e) {
      runInAction(() => {
        this.myProfile = null;
        this.myEngagements = [];
      });
      this.handleError(e);
    }
  };

  fetchGroupEngagements = async (): Promise<void> => {
    try {
      const { engagements } = await this.q(TalentManagerEngagementService.listGroupEngagements());
      runInAction(() => {
        this.groupEngagements = engagements;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  fetchMyEngagements = async (): Promise<void> => {
    try {
      const { engagements } = await this.q(TalentManagerEngagementService.listMyEngagements());
      runInAction(() => {
        this.myEngagements = engagements;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  fetchCreditPackages = async (): Promise<void> => {
    try {
      const { creditPackages } = await this.q(CreditsService.listCreditPackages());
      runInAction(() => {
        this.creditPackages = creditPackages;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  fetchCreditBalance = async (engagementId: string): Promise<TEngagementCreditBalanceResponse | null> => {
    try {
      return await this.q(TalentManagerEngagementService.getCreditBalance(engagementId));
    } catch (e) {
      this.handleError(e);
      return null;
    }
  };

  getProfile = async (
    id: string,
    projectId?: string
  ): Promise<{ profile: TalentManagerProfile; hasWorkedWith: boolean } | null> => {
    try {
      const { talentManagerProfile, has_worked_with } = await this.q(
        TalentManagerService.getTalentManager(id, projectId)
      );
      if (!talentManagerProfile) return null;
      return { profile: talentManagerProfile, hasWorkedWith: Boolean(has_worked_with) };
    } catch (e) {
      this.handleError(e);
      return null;
    }
  };

  submitReview = async (
    profileId: string,
    rating: number,
    reviewText: string,
    projectId?: string
  ): Promise<boolean> => {
    try {
      await this.q(
        TalentManagerService.createTalentManagerReview(profileId, {
          rating,
          review_text: reviewText,
          project_id: projectId,
        })
      );
      return true;
    } catch (e) {
      this.handleError(e);
      return false;
    }
  };

  createEngagement = async (profileId: string, projectId: string): Promise<TalentManagerEngagement | null> => {
    try {
      const { engagement } = await this.q(
        TalentManagerEngagementService.createEngagement({ talent_manager_profile_id: profileId, project_id: projectId })
      );
      if (!engagement) return null;
      runInAction(() => {
        this.groupEngagements = [...this.groupEngagements, engagement];
      });
      return engagement;
    } catch (e) {
      this.handleError(e);
      return null;
    }
  };

  updateEngagement = async (id: string, canEditProject: boolean): Promise<void> => {
    try {
      const { engagement } = await this.q(
        TalentManagerEngagementService.updateEngagement(id, { can_edit_project: canEditProject })
      );
      if (!engagement) return;
      runInAction(() => {
        this.groupEngagements = this.groupEngagements.map((e) => (e.id === id ? engagement : e));
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  createCheckout = async (packageId: string, engagementId: string, returnUrl: string): Promise<string | null> => {
    try {
      const { checkout_url } = await this.q(
        CreditsService.createCreditCheckout({ credit_package_id: packageId, engagement_id: engagementId, return_url: returnUrl })
      );
      return checkout_url;
    } catch (e) {
      this.handleError(e);
      return null;
    }
  };

  registerTM = async (data: TRegisterTalentManagerRequest): Promise<TalentManagerProfile | null> => {
    try {
      const { talentManagerProfile } = await this.q(TalentManagerService.registerTalentManager(data));
      runInAction(() => {
        this.myProfile = talentManagerProfile;
      });
      return talentManagerProfile;
    } catch (e) {
      this.handleError(e);
      return null;
    }
  };

  updateMyProfile = async (id: string, data: Partial<TRegisterTalentManagerRequest>): Promise<void> => {
    try {
      const { talentManagerProfile } = await this.q(TalentManagerService.updateTalentManager(id, data));
      runInAction(() => {
        this.myProfile = talentManagerProfile;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  approveTM = async (id: string): Promise<void> => {
    try {
      await this.q(TalentManagerService.approveTalentManager(id));
      runInAction(() => {
        this.profiles = this.profiles.map((p) => (p.id === id ? { ...p, approval_status: 'approved' as TalentManagerProfile['approval_status'] } : p));
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  suspendTM = async (id: string): Promise<void> => {
    try {
      await this.q(TalentManagerService.suspendTalentManager(id));
      runInAction(() => {
        this.profiles = this.profiles.map((p) => (p.id === id ? { ...p, approval_status: 'suspended' as TalentManagerProfile['approval_status'] } : p));
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  logManualTime = async (data: TLogManualTimeRequest): Promise<void> => {
    try {
      await this.q(TalentManagerTimeService.logManualTime(data));
      await this.fetchMyEngagements();
    } catch (e) {
      this.handleError(e);
    }
  };

  approveTimeEntry = async (id: string, isBilled: boolean): Promise<boolean> => {
    try {
      await this.q(TalentManagerTimeService.approveTimeEntry(id, { is_billed: isBilled }));
      await this.fetchGroupEngagements();
      return true;
    } catch (e) {
      this.handleError(e);
      return false;
    }
  };

  getConnectOnboardingLink = async (returnUrl: string): Promise<string | null> => {
    try {
      const { onboarding_url } = await this.q(StripeConnectService.getOnboardingLink({ return_url: returnUrl }));
      return onboarding_url;
    } catch (e) {
      this.handleError(e);
      return null;
    }
  };

  createConnectAccount = async (): Promise<void> => {
    try {
      await this.q(StripeConnectService.createConnectAccount());
    } catch (e) {
      this.handleError(e);
    }
  };

  uploadProfilePhoto = async (photo: File): Promise<void> => {
    try {
      const { talentManagerProfile } = await this.q(TalentManagerService.uploadMyProfilePhoto({ photo }));
      runInAction(() => {
        this.myProfile = talentManagerProfile;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  deleteProfilePhoto = async (): Promise<void> => {
    try {
      const { talentManagerProfile } = await this.q(TalentManagerService.deleteMyProfilePhoto());
      runInAction(() => {
        this.myProfile = talentManagerProfile;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  getProfilePhotoUrl = async (profileId: string): Promise<string | null> => {
    try {
      const { url } = await this.q(TalentManagerService.getTalentManagerPhotoUrl(profileId));
      return url;
    } catch (e) {
      return null;
    }
  };

  parseResume = async (resume: File): Promise<TParseResumeResponse | null> => {
    try {
      return await this.q(TalentManagerService.parseResume({ resume }));
    } catch (e) {
      this.handleError(e);
      return null;
    }
  };

  uploadResume = async (resume: File): Promise<void> => {
    try {
      const { talentManagerProfile } = await this.q(TalentManagerService.uploadMyResume({ resume }));
      runInAction(() => {
        this.myProfile = talentManagerProfile;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  deleteResume = async (): Promise<void> => {
    try {
      const { talentManagerProfile } = await this.q(TalentManagerService.deleteMyResume());
      runInAction(() => {
        this.myProfile = talentManagerProfile;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  getResumeUrl = async (profileId: string): Promise<string | null> => {
    try {
      const { url } = await this.q(TalentManagerService.getTalentManagerResumeUrl(profileId));
      return url;
    } catch (e) {
      return null;
    }
  };
}
